import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Sentry } from './lib/sentry'
import SentryErrorFallback from './components/SentryErrorFallback'
import { ThemeProvider } from './components/theme-provider'
import { AuthProvider } from './context/AuthContext'
import { ToastProvider } from './context/ToastContext'
import { ProductProvider } from './context/ProductContext'
import { OrderProvider } from './context/OrderContext'
import { TransactionProvider } from './context/TransactionContext'
import { SessionRefreshBanner } from './components/SessionRefreshBanner'
import { SessionRefreshBridge } from './components/SessionRefreshBridge'
import { UIProvider } from './context/UIContext'
import { DashboardLayout } from './layouts/DashboardLayout'
import DashboardHome from './pages/DashboardHome'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import ProductCreate from './pages/products/ProductCreate'
import ProductDetail from './pages/products/ProductDetail'
import ProductEdit from './pages/products/ProductEdit'
import CategoryList from './pages/categories/CategoryList'
import CategoryDetail from './pages/categories/CategoryDetail'
import ProductList from './pages/products/ProductList'
import OrderList from './pages/orders/OrderList'
import OrderDetail from './pages/orders/OrderDetail'
import CustomerList from './pages/customers/CustomerList'
import CustomerDetail from './pages/customers/CustomerDetail'
import AnalyticsPage from './pages/analytics/AnalyticsPage'
import TransactionList from './pages/transactions/TransactionList'
import TransactionDetail from './pages/transactions/TransactionDetail'
import BillingPage from './pages/billing/BillingPage'
import StripeConnectPage from './pages/payments/StripeConnectPage'
import StorefrontEditor from './pages/storefront/StorefrontEditor'
import SpecialOffersPage from './pages/specialOffers/SpecialOffersPage'
import DiscountCodesPage from './pages/discountCodes/DiscountCodesPage'
import HomepageSectionsPage from './pages/homepage/HomepageSectionsPage'
import HomepageSectionProductsPage from './pages/homepage/HomepageSectionProductsPage'
import SpecialOfferProductsPage from './pages/specialOffers/SpecialOfferProductsPage'
import ChangePassword from './pages/ChangePassword'
import SupportAccessPage from './pages/SupportAccessPage'
import CommerceHubPage from './pages/commerce/CommerceHubPage'
import PickupLocationsPage from './pages/pickup/PickupLocationsPage'
import WithdrawalsPage from './pages/withdrawals/WithdrawalsPage'
import ContactMessagesPage from './pages/contactMessages/ContactMessagesPage'
import NewsletterSubscribersPage from './pages/newsletter/NewsletterSubscribersPage'
import TeamPage from './pages/team/TeamPage'
import Settings from './pages/Settings'
import StoreContentTranslations from './pages/StoreContentTranslations'
import HelpCenter from './pages/HelpCenter'
import { MakeSalesPage, SalesHistoryPage } from './features/pos'

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/support-access" element={<SupportAccessPage />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route element={<DashboardLayout />}>
        <Route index element={<DashboardHome />} />
        <Route path="categories" element={<CategoryList />} />
        <Route path="categories/:idOrSlug" element={<CategoryDetail />} />
        <Route path="products" element={<ProductList />} />
        <Route path="products/new" element={<ProductCreate />} />
        <Route path="products/:id" element={<ProductDetail />} />
        <Route path="products/:id/edit" element={<ProductEdit />} />
        <Route path="orders" element={<OrderList />} />
        <Route path="orders/:orderRef" element={<OrderDetail />} />
        <Route path="customers" element={<CustomerList />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="transactions" element={<TransactionList />} />
        <Route path="transactions/:id" element={<TransactionDetail />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="commerce" element={<CommerceHubPage />} />
        <Route path="pickup-locations" element={<PickupLocationsPage />} />
        <Route path="withdrawals" element={<WithdrawalsPage />} />
        <Route path="payments/stripe" element={<StripeConnectPage />} />
        <Route path="storefront" element={<StorefrontEditor />} />
        <Route path="homepage-sections" element={<HomepageSectionsPage />} />
        <Route path="homepage-sections/:sectionId/products" element={<HomepageSectionProductsPage />} />
        <Route path="special-offers/:offerId/products" element={<SpecialOfferProductsPage />} />
        <Route path="special-offers" element={<SpecialOffersPage />} />
        <Route path="discount-codes" element={<DiscountCodesPage />} />
        <Route path="contact-messages" element={<ContactMessagesPage />} />
        <Route path="newsletter-subscribers" element={<NewsletterSubscribersPage />} />
        <Route path="settings" element={<Settings />} />
        <Route path="translations" element={<StoreContentTranslations />} />
        <Route path="help-center" element={<HelpCenter />} />
        <Route path="billing/*" element={<BillingPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="pos/sales" element={<MakeSalesPage />} />
        <Route path="pos/history" element={<SalesHistoryPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>
    <BrowserRouter>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem
        disableTransitionOnChange
      >
        <AuthProvider>
          <ToastProvider>
          <ProductProvider>
            <OrderProvider>
              <TransactionProvider>
                <UIProvider>
                  <SessionRefreshBridge />
                  <SessionRefreshBanner />
                  <AppRoutes />
                </UIProvider>
              </TransactionProvider>
            </OrderProvider>
          </ProductProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}
